import { BrandTypes } from '@ps/platform-message-framework/common/BrandTypes';
import { LicenceId } from '@ps/platform-message-framework/common/LicenceId';
import { Sites } from '@ps/platform-message-framework/common/Sites';
import { Cookie as CookieNames, FeatureFlag, ResolverTag } from '@ps/web-core-enums';
import { Size as ModalSize } from '@ps/web-core-store';
import { getWindow } from '@ps/web-core-utils';
import { CashierPath } from '@ps/web-pam-types';
import classNames from 'clsx';
import { get, isEmpty, omit, pickBy } from 'lodash';
import Cookies from 'universal-cookie';

import { PostMessage } from '@swf/core/enums';
import { getState, select } from '@swf/core/store';
import type {
  HandleMessageProps,
  ResolverProps,
  ResolverQuery
} from '@swf/core/types/AccountResolver';
import { BonusTags, Department, RollType } from '@swf/core/types/AccountResolver';
import { MiniGame } from '@swf/core/types/MiniGame';
import { encodeQuery } from '@swf/core/utils';

const cookies = new Cookies();

const iframeModalStyle = (styles?: { iframeModal: string }) => ({
  className: styles?.iframeModal,
  size: ModalSize.EXTRA_LARGE
});

export const MINIGAME_TAGS = {
  [MiniGame.TheDeal1]: 'play-the-deal-webui',
  [MiniGame.TheDeal2]: 'play-the-deal-webui',
  [MiniGame.TheDeal3]: 'play-the-deal-webui',
  [MiniGame.SingleReel]: 'singlereel_game',
  [MiniGame.PickObject]: 'pickobject_game',
  [MiniGame.BonusWheel]: 'bonuswheel_game',
  [MiniGame.MatchDayMillions]: 'matchdaymillions_game'
};

export const getBonusPath = (rollType: RollType) => {
  switch (rollType) {
    case RollType.InstantBonuses:
      return 'instant-bonuses';
    case RollType.FreeSpinBonuses:
      return 'free-spin-bonuses';
    case RollType.GameTickets:
      return 'game-tickets';
  }
};

export const getAccountResolverProps = ({
  assetUrl,
  brand,
  brandName,
  close,
  country,
  emailValidated,
  embedded,
  embeddedRoute,
  firstSession,
  getTokenAndSignature,
  isPlayMoney,
  isRealMoneyOK,
  language,
  license,
  searchParams,
  migrationRequired,
  migrationTarget,
  navigate,
  params,
  pathname,
  platform,
  push,
  setPendingNotifications,
  site,
  styles,
  isReloader,
  features,
  generatePath
}: ResolverProps) => {
  const pmcPaywall = features[FeatureFlag.PmcPaywall];
  const ice2Feature = features[FeatureFlag.Ice2Feature];
  const isPRHubEnabled = features[FeatureFlag.PRHub];
  const hspSwcDeposit = features[FeatureFlag.SwcDeposit];
  const isSWCWithdraw = features[FeatureFlag.SwcWithdrawal];
  const isSWCCashierHome = features[FeatureFlag.SwcCashier];
  const isSWCP2pTransfer = features[FeatureFlag.SwcP2pTransfer];
  const hspSwcDepositReloader = features[FeatureFlag.SwcDepositReloader];
  const isSWCManageCurrencies = features[FeatureFlag.SwcManageCurrencies];
  const isSwcRmfEnabled = features[FeatureFlag.SwcRmf];
  const disableNewUkAgeVerificationFlow = features[FeatureFlag.DisableNewUkAgeVerificationFlow];
  const shouldAddTokensToCowUrl = features[FeatureFlag.CowAddTokenAndSignatureToUrl];
  const isSWCDeposit =
    (hspSwcDeposit && !isReloader) || (hspSwcDeposit && isReloader && hspSwcDepositReloader);
  const domain = select.location.getCookieDomain(getState());
  const port = select.location.getPort(getState());
  const brandId = select.cms.getBrandId(getState());

  switch (true) {
    case !!(pathname.startsWith('/account/cashier') && params.department): {
      const { department } = params;
      const isPaywall = department === Department.Paywall;
      const isRealMoneyForm = department === Department.RMF;
      const query: ResolverQuery = { applePaySupported: false };
      const mitIdData =
        isRealMoneyForm && !isEmpty(omit(searchParams, 'temporaryPassword'))
          ? searchParams
          : undefined;

      const tag = {
        [Department.Balances]: 'wc2fe_balance',
        [Department.BankID]: 'open_bankid_validation',
        [Department.ConvertCurrencies]: 'wc2fe_convert_currencies',
        [Department.PreferredCurrency]: 'wc2fe_preferred_currency',
        [Department.Deposit]: 'wc2fe_deposit',
        [Department.History]: 'wc2fe_history',
        [Department.Home]: 'wc2fe_home',
        [Department.Paywall]: pmcPaywall
          ? ResolverTag.PMCPaywall
          : ResolverTag.FreemiumBuyPlayChips,
        [Department.PlayMoney]: 'wc2fe_playmoney',
        [Department.P2pTransfer]: 'wc2fe_transfer',
        [Department.RMF]: 'web-real-money-form',
        [Department.Withdraw]: 'wc2fe_cashout'
      }[department];

      if (
        !isPlayMoney &&
        !emailValidated &&
        // @ts-ignore department is valid
        ![Department.Deposit, Department.Home].includes(department) &&
        !(Department.Withdraw === department && isSWCWithdraw) &&
        !isRealMoneyForm
      ) {
        close?.('/account/cashier');
        setPendingNotifications();
      } else if (migrationRequired && department !== Department.Withdraw) {
        push?.('/account/migration');
      }

      if (isRealMoneyForm && !disableNewUkAgeVerificationFlow) {
        query.opencontext = firstSession ? 'registration' : 'login';
      } else if (isPaywall) {
        query.ram = 1; // to show close button
      }

      if (Department.Deposit === department && isSWCDeposit) {
        close?.('/account');
        navigate(CashierPath.Deposit);
      } else if (Department.History === department && isSWCCashierHome) {
        close?.('/account');
        navigate(CashierPath.TransactionHistory);
      } else if (
        [Department.Home, Department.Balances, Department.PlayMoney].includes(department) &&
        isSWCCashierHome
      ) {
        close?.('/account');
        navigate(generatePath?.() || '');
      } else if (Department.Withdraw === department && isSWCWithdraw) {
        close?.('/account');
        navigate(CashierPath.Withdraw);
      } else if (
        department &&
        [Department.ConvertCurrencies, Department.PreferredCurrency].includes(department) &&
        isSWCManageCurrencies
      ) {
        close?.('/account');
        navigate(CashierPath.ManageCurrencies);
      } else if (Department.P2pTransfer === department && isSWCP2pTransfer) {
        close?.('/account');
        navigate(CashierPath.P2pTransfer);
      } else if (Department.RMF === department && isSwcRmfEnabled) {
        close?.('/account');
        navigate(CashierPath.RealMoneyForm);
      }

      try {
        query.applePaySupported = !!ApplePaySession && ApplePaySession.canMakePayments();
      } catch {
        // ignore
      }

      const host = isPaywall ? 'playchips' : 'cashier';

      return {
        resolverProps: {
          tag,
          query: { ...query, brand: brandId },
          mitIdData,
          host: `${host}.${domain}`,
          port,
          className: isPaywall ? (embeddedRoute ? styles?.iframeFullWindow : '') : styles?.overlay,
          allow: 'clipboard-read; clipboard-write'
        },
        styles: isPaywall
          ? {
              size: ModalSize.MEDIUM,
              className: classNames(styles?.iframeModal, pmcPaywall && styles?.paywall),
              hidden: false
            }
          : { hidden: true }
      };
    }
    case !!(
      (pathname.startsWith('/account/ice') || pathname.startsWith('/prhub/ice')) &&
      params.icegame
    ): {
      const { icegame, icegameId, trigger } = params;
      const iceParam = icegame === 'leaderboards' ? 'leaderboardid' : 'promoid';
      const hostingApp = pathname.startsWith('/prhub/ice') && isPRHubEnabled ? 'prhub' : undefined;
      const query = { brandName, site, [iceParam]: icegameId, trigger, hostingApp, platform };
      const challengesStyles =
        pathname.indexOf('/prhub/ice') > -1 && isPRHubEnabled ? null : iframeModalStyle(styles);
      let tag;
      if (icegame === 'leaderboards') tag = ResolverTag.IceLeaderboards;
      else if (ice2Feature) tag = ResolverTag.Ice2Home;
      else tag = ResolverTag.IceHome;

      return {
        resolverProps: { tag, query, scrolling: 'yes' },
        styles: challengesStyles
      };
    }
    case pathname.startsWith('/account/migration'): {
      if (embedded) {
        getWindow()?.starsweb?.app.execute('migrate');
        close?.('/account');
        break;
      }
      const { token, signature } = getTokenAndSignature();
      const query = {
        type: 'web',
        gver: 'WEB',
        country,
        ram: 1,
        token,
        signature,
        language,
        site,
        product: brand === BrandTypes.STARS_CASINO ? 'PokerStarsCasino' : brandName,
        devIx: cookies.get(CookieNames.INSTALL_ID),
        license,
        migrationTarget: migrationTarget || -1
      };

      return {
        resolverProps: {
          tag: 'web-migration-form',
          query,
          className: styles?.overlay,
          scrolling: 'auto'
        },
        styles: { hidden: true }
      };
    }
    case pathname.startsWith('/account/the-deal'): {
      if (!isRealMoneyOK) {
        push?.('/account/cashier/rmf');
        break;
      } else {
        const query = { ram: 1, isWeb: true, type: 'web', brand: brandName, site };

        return {
          resolverProps: { tag: 'play-the-deal-webui', query },
          styles: iframeModalStyle(styles)
        };
      }
    }
    case pathname.startsWith('/account/bonus-game'): {
      const tag =
        MINIGAME_TAGS[searchParams?.minigameType as keyof typeof MINIGAME_TAGS] ||
        MINIGAME_TAGS[MiniGame.SingleReel];
      const query = {
        ...(searchParams || {}),
        ram: 1,
        isWeb: true,
        type: 'web',
        brand: brandName,
        site
      };

      return {
        resolverProps: {
          tag,
          query,
          style: site === Sites.IT_SISAL_EXT ? { height: '100vh' } : null
        },
        styles: iframeModalStyle(styles)
      };
    }
    case pathname.startsWith('/account/open-chest'): {
      const { chest } = searchParams || {};
      const base = assetUrl.includes('.html') ? undefined : assetUrl;
      const { token, signature } = getTokenAndSignature();

      return {
        resolverProps: {
          tag: 'starsrewards_chest2',
          query: pickBy({
            chest,
            base,
            ...(shouldAddTokensToCowUrl && { token, signature })
          }),
          style: site === Sites.IT_SISAL_EXT ? { height: '100vh' } : null
        },
        styles: iframeModalStyle(styles)
      };
    }
    case !!(
      (pathname.startsWith('/account/my-stars/bonus') ||
        pathname.startsWith('/prhub/my-rewards/bonus')) &&
      params.bonusType
    ): {
      const { bonusType, bonusId } = params;
      const hostingApp =
        pathname.startsWith('/prhub/my-rewards/bonus') && isPRHubEnabled ? 'prhub' : undefined;

      return {
        resolverProps: {
          tag: BonusTags[bonusType as keyof typeof BonusTags],
          query: pickBy({ bonusid: bonusId, brand, hostingApp, platform }),
          className: styles?.inlineIframe,
          scrolling: 'yes',
          style: isPRHubEnabled ? { height: '100%', display: 'block' } : null
        },
        styles: null
      };
    }
    default: {
      return {
        resolverProps: null,
        styles: null
      };
    }
  }
};

let isRmfCompleted = false;
export const handleResolverPostMessage = async ({
  data,
  depositLimitPath,
  modalReplace,
  push,
  logout,
  close,
  dataIntegrityCheckFailed,
  refreshAccount,
  quickAccountCreationRequired,
  license,
  c2lHandler,
  setMitIdWc2Data
}: HandleMessageProps) => {
  if (typeof data === 'object') {
    if (
      data.command === PostMessage.OPEN_ACCOUNT &&
      data.arguments[0] === 'root.responsible.restrictDepositLimit'
    ) {
      modalReplace([{ path: depositLimitPath }]);
      return;
    }
    const { msgId, ...messageData } = data;
    switch (msgId) {
      case PostMessage.OPEN_MINIGAME:
        push({ pathname: '/account/bonus-game', search: encodeQuery(messageData) });
        return;
      case PostMessage.C2L: {
        c2lHandler(data.data.c2l);
        return;
      }
      case PostMessage.OPEN_MITID: {
        setMitIdWc2Data(messageData);
        return;
      }
      case PostMessage.OPEN_BONUSMANAGER: {
        const path = getBonusPath(data.rollType);
        path && modalReplace([{ path: `/account/my-stars/bonus/${path}` }]);
        return;
      }
    }

    const redirect = get(data, ['ps_openwebsite', 'webURL']);
    if (redirect) {
      window.location.href = redirect;
      return;
    }
  } else {
    const messageId: string = data.startsWith('{') ? JSON.parse(data).msgId : data;

    switch (messageId) {
      case PostMessage.CLOSE_MINIGAME:
        close?.('/account/bonus-game');
        close?.('/account/the-deal');
        return;
      case PostMessage.CLOSE_DEPOSIT: {
        if (dataIntegrityCheckFailed && !isRmfCompleted) {
          await logout();
        }

        close?.('/account');
        return;
      }
      case PostMessage.CLOSE_COMPLETED_RMF: {
        isRmfCompleted = true;
        await refreshAccount();
        return;
      }
      case PostMessage.CLOSE_ICE:
      case PostMessage.CLOSE_SRWRDCHESTS:
      case PostMessage.CLOSE_PMCASHIER: {
        close?.('/account');
        return;
      }
      case PostMessage.CLOSE_LICENSE_MIGRATION: {
        if (quickAccountCreationRequired) {
          await logout();
        }

        close?.('/account');
        return;
      }
      case PostMessage.LICENSE_MIGRATION: {
        push('/account/migration');
        return;
      }
      case PostMessage.LOGOUT: {
        await logout();
        return;
      }
      case PostMessage.LOGIN_TIME_LIMITS_NOT_COMPLETED: {
        await logout();
        return;
      }
      case PostMessage.AGE_VERIFICATION_COMPLETED: {
        license === LicenceId.UK
          ? push?.('/account/cashier/deposit')
          : push('/auth/registration/welcome');
        return;
      }
      case PostMessage.ACCOUNT_RMF: {
        push('/account/cashier/rmf');
        return;
      }
      case PostMessage.OPEN_DEPOSIT:
        push('/account/cashier/deposit');
        return;
      case PostMessage.OPEN_VERIFICATION:
        push('/auth/docverify');
        return;
      case PostMessage.OPEN_PAY_WALL:
        push('/account/cashier/paywall');
        return;
      case PostMessage.VERIFY_EMAIL:
        push({
          pathname: '/notifications/confirm-email',
          search: encodeQuery({ sendEmail: true })
        });
        return;
      case PostMessage.OPEN_LUGAS_LIMIT_REGISTRATION:
        push({ pathname: '/notifications/cross-operator-deposit-limit' });
        return;
      case PostMessage.REFRESH_ACCOUNT:
        await refreshAccount();
        return;
    }
  }
};
